import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Combobox,
  Group,
  Input,
  ScrollArea,
  Text,
  useCombobox,
} from "@mantine/core";
import {
  AccessionLogPrefixOption,
  AccessionLogPrefixSelection,
  prefixSelectionFromIds,
  prefixSelectionLabel,
} from "../../types/accessionLogPrefix";

interface AccessionLogPrefixFilterProps {
  options: AccessionLogPrefixOption[];
  value: AccessionLogPrefixSelection;
  onChange: (value: AccessionLogPrefixSelection) => void;
  loading?: boolean;
  disabled?: boolean;
}

export function AccessionLogPrefixFilter({
  options,
  value,
  onChange,
  loading,
  disabled,
}: AccessionLogPrefixFilterProps) {
  const [search, setSearch] = useState("");
  const combobox = useCombobox();
  const label = useMemo(() => prefixSelectionLabel(value), [value]);
  const selectedIds = useMemo(
    () =>
      value.prefixMode === "all"
        ? options.map(({ id }) => id)
        : value.prefixIds ?? [],
    [options, value]
  );
  const visibleOptions = useMemo(
    () =>
      options.filter(({ code }) =>
        code.toLowerCase().includes(search.trim().toLowerCase())
      ),
    [options, search]
  );
  const handleToggle = useCallback(() => {
    combobox.toggleDropdown();
    combobox.focusSearchInput();
  }, [combobox]);
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
      combobox.resetSelectedOption();
    },
    [combobox]
  );
  const handleSelect = useCallback(
    (id: string) => {
      const next = selectedIds.includes(id)
        ? selectedIds.filter((selected) => selected !== id)
        : [...selectedIds, id];
      onChange(
        prefixSelectionFromIds(
          next,
          options.map((option) => option.id)
        )
      );
    },
    [onChange, options, selectedIds]
  );
  const handleSelectAll = useCallback(
    () => onChange({ prefixMode: value.prefixMode === "all" ? "none" : "all" }),
    [onChange, value.prefixMode]
  );
  const handleUnselectAll = useCallback(
    () => onChange({ prefixMode: "none" }),
    [onChange]
  );

  return (
    <Input.Wrapper label="Prefixes">
      <Combobox
        store={combobox}
        onOptionSubmit={handleSelect}
        position="bottom-start"
        offset={8}
        withinPortal
        width={300}
      >
        <Combobox.Target targetType="button" withExpandedAttribute>
          <Button
            display="block"
            variant="default"
            onClick={handleToggle}
            aria-label={`Prefixes: ${label}`}
            rightSection={<Combobox.Chevron />}
            disabled={disabled}
            loading={loading}
          >
            {label}
          </Button>
        </Combobox.Target>
        <Combobox.Dropdown>
          <Combobox.Search
            value={search}
            onChange={handleSearch}
            placeholder="Search prefixes"
            aria-label="Search prefixes"
          />
          <Group justify="space-between" p="xs" gap="xs">
            <Checkbox
              label="Select all prefixes"
              checked={value.prefixMode === "all"}
              indeterminate={value.prefixMode === "selected"}
              onChange={handleSelectAll}
            />
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={handleUnselectAll}
            >
              Unselect all
            </Button>
          </Group>
          <ScrollArea.Autosize mah={240} type="auto">
            <Combobox.Options aria-label="Prefixes" aria-multiselectable="true">
              {visibleOptions.map((option) => (
                <Combobox.Option
                  key={option.id}
                  value={option.id}
                  active={selectedIds.includes(option.id)}
                  aria-selected={selectedIds.includes(option.id)}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Checkbox.Indicator
                      checked={selectedIds.includes(option.id)}
                      aria-hidden
                    />
                    <Text size="sm">
                      {option.code}
                      {option.isActive ? "" : " (inactive)"}
                    </Text>
                  </Group>
                </Combobox.Option>
              ))}
              {!visibleOptions.length && (
                <Combobox.Empty>No prefixes found</Combobox.Empty>
              )}
            </Combobox.Options>
          </ScrollArea.Autosize>
          <Combobox.Footer>
            <Text size="xs" c="dimmed">
              {label}. Bulk actions affect all prefixes.
            </Text>
          </Combobox.Footer>
        </Combobox.Dropdown>
      </Combobox>
    </Input.Wrapper>
  );
}
